export const coerceString = (value) =>
  typeof value === "string" ? value : value == null ? "" : String(value);

export const parseBoolean = (value) => {
  if (/^(true|yes|y|1)$/i.test(value)) return true;
  if (/^(false|no|n|0)$/i.test(value)) return false;
  return null;
};

export const parseNumber = (value, parser) => {
  const normalized = String(value).replace(/,/g, "").trim();
  if (!normalized) return null;
  const parsed = parser(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parseDateOnly = (value) => {
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

export const parseDateTime = (value) => {
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

export const parseTime = (value) => {
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    const [hours, minutes] = trimmed.split(":");
    return `${hours.padStart(2, "0")}:${minutes}`;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(11, 16);
};

/**
 * Normalize a currency code to a 3-letter ISO code when present in the cell.
 * Currency symbols (€, $, …) are not converted — use ISO codes in Excel.
 */
export const normalizeCurrencyCode = (raw) => {
  if (raw == null) return "";
  const upper = String(raw).trim().toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : "";
};

export const parseCurrency = (value) => {
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  // CODE then amount: e.g. EUR 25, EUR25
  const codeFirst = trimmed.match(/^([A-Za-z]{3})\s*(-?[\d,.]+)$/);
  if (codeFirst) {
    return finalizeParsedCurrency({
      amount: codeFirst[2].replace(/,/g, ""),
      currencyCode: normalizeCurrencyCode(codeFirst[1]),
    });
  }

  // amount then CODE: e.g. 25 EUR, 25.00 EUR
  const amountFirst = trimmed.match(/^(-?[\d,.]+)\s*([A-Za-z]{3})$/);
  if (amountFirst) {
    return finalizeParsedCurrency({
      amount: amountFirst[1].replace(/,/g, ""),
      currencyCode: normalizeCurrencyCode(amountFirst[2]),
    });
  }

  return null;
};

export const parseUrl = (asString) => {
  if (/^https?:\/\//i.test(asString)) return asString;
  if (/^[a-z][a-z0-9+.-]*:/i.test(asString)) return asString;
  return `https://${asString.replace(/^\/+/, "")}`;
};

export const parseStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => coerceString(item).trim()).filter(Boolean);
  }
  const trimmed = String(value).trim();
  if (!trimmed) return [];
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => coerceString(item).trim()).filter(Boolean);
      }
    } catch {
      // fall through to delimiter split
    }
  }
  return trimmed
    .split(/[,;|]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const parsePeriod = (value) => {
  if (value && typeof value === "object") {
    const startDate = parseDateOnly(
      value.startDate ?? value.startDateTime ?? value.start ?? ""
    );
    const endDate = parseDateOnly(value.endDate ?? value.endDateTime ?? value.end ?? "");
    if (startDate || endDate) {
      return { startDate, endDate };
    }
    return null;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const rangeMatch = trimmed.match(/^(.+?)\s*(?:to|–|—|-)\s*(.+)$/i);
  if (rangeMatch) {
    const startDate = parseDateOnly(rangeMatch[1]);
    const endDate = parseDateOnly(rangeMatch[2]);
    if (startDate || endDate) {
      return { startDate, endDate };
    }
  }

  const singleDate = parseDateOnly(trimmed);
  if (singleDate) {
    return { startDate: singleDate, endDate: null };
  }

  return null;
};

export const coerceISOString = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.$date) return value.$date;
  return null;
};

export const coerceDateOnly = (value) => {
  const iso = coerceISOString(value);
  if (!iso) return parseDateOnly(value);
  return iso.slice(0, 10);
};

export const coerceTimeOnly = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{2}:\d{2}/.test(trimmed)) {
      return trimmed.slice(0, 5);
    }
  }
  const iso = coerceISOString(value);
  if (!iso) return parseTime(value);
  return iso.slice(11, 16);
};

export const coerceDateTimeLocal = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 16);
  if (typeof value === "object" && value.$date) return String(value.$date).slice(0, 16);
  return null;
};

export const normalizeCurrencyObject = (value) => {
  if (!value || typeof value !== "object") {
    return { amount: "", currencyCode: "" };
  }
  return {
    amount: value.amount == null ? "" : String(value.amount).trim(),
    currencyCode: normalizeCurrencyCode(value.currencyCode),
  };
};

export const toInputDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

export const sanitizeCurrencyForApi = (value) => {
  const currency = normalizeCurrencyObject(value);
  const amount = currency.amount.trim();
  const currencyCode = currency.currencyCode.trim().toUpperCase();
  if (!amount && !currencyCode) return null;
  // Backend rejects partial CURRENCY (amount-only or code-only).
  if (!amount || !currencyCode || !/^[A-Z]{3}$/.test(currencyCode)) {
    return null;
  }
  return { amount, currencyCode };
};

const finalizeParsedCurrency = ({ amount, currencyCode }) =>
  sanitizeCurrencyForApi({ amount, currencyCode });
